import { Label } from './ui/label'
import { toast } from './ui/use-toast'

import { Switch } from '@/components/ui/switch'
import { useAuth } from '@/providers'
import {
  useMutationPutOrgSnapshotSchedule,
  useQueryGetOrgSnapshotSchedule
} from '@/services/snapshot.service'
import { isPermitted, Permissions } from '@/utils/permission-manager'

type Props = {
  orgId: string
  disabled?: boolean
}

/**
 * Staff-only per-org toggle for scheduled (daily) snapshots, backed by
 * snapshot-api (`/v1/org-snapshot-schedule`). This replaces the customer-facing
 * toggle in epilot360-configuration-hub (gated off via the
 * `enable-daily-snapshots-frontend` flag).
 *
 * On  → PUT { enabled: true }  (snapshot-api fills a thundering-herd-safe,
 *       org-hashed off-peak cron + 90-day retention on first enable).
 * Off → PUT { enabled: false } (keeps the row + its cron/retention, tears down
 *       the EventBridge schedule via reconcile).
 */
const OrganizationSnapshotSchedule = ({ orgId, disabled = false }: Props) => {
  const { userData } = useAuth()
  const { data: schedule, isLoading } = useQueryGetOrgSnapshotSchedule(orgId)
  const putSchedule = useMutationPutOrgSnapshotSchedule(orgId)

  const enabled = schedule?.enabled ?? false
  const loading = isLoading || putSchedule.isPending

  const onToggle = (next: boolean) => {
    putSchedule.mutate(
      { enabled: next },
      {
        onSuccess: () =>
          toast({
            title: 'Success',
            description: `Scheduled snapshots ${next ? 'enabled' : 'disabled'}`
          }),
        onError: (err) =>
          toast({
            title: 'Error',
            description: `Failed to update scheduled snapshots. ${err?.['message'] ?? ''}`
          })
      }
    )
  }

  return (
    <div className="px-4 py-2 mt-4 space-y-2 border border-border rounded-md">
      <div className="flex flex-row items-center justify-between">
        <div className="space-y-1">
          <Label htmlFor="scheduled-snapshots">Scheduled Snapshots</Label>
          <p className="text-xs text-muted-foreground">
            Daily configuration snapshots for this org (off-peak, 90-day
            retention).
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={
            !isPermitted(Permissions.EDIT_ORG_SETTINGS, userData) ||
            disabled ||
            loading
          }
          id="scheduled-snapshots"
          loading={loading}
          onCheckedChange={onToggle}
          style={{ marginTop: 0 }}
        />
      </div>
    </div>
  )
}

export default OrganizationSnapshotSchedule
